import React, { useEffect, useState } from "react";
import {
  FaBolt,
  FaCogs,
  FaPlus,
  FaEdit,
  FaTrash,
} from "react-icons/fa";
import { MdElevator } from "react-icons/md";
import { AddDeviceModal } from "./AddDeviceModal";
import { UpdateDeviceStatusModal } from "./UpdateDeviceStatusModal";
import { API_BASE } from '../config'

export function SmartDevices({
  devices: initialDevices,
  onDeviceSettings,
  onAddDevice,
  onUpdateStatus,
  onDeleteDevice,
  trialMaxDevices,
  isTrial,
}) {
  const [devices, setDevices] = useState(initialDevices || [])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [user, setUser] = useState(null)
  const [isAddDeviceOpen, setIsAddDeviceOpen] = useState(false)
  const [isUpdateDeviceOpen, setIsUpdateDeviceOpen] = useState(false)
  const [selectedDeviceForUpdate, setSelectedDeviceForUpdate] = useState(null)
  const [expandedSections, setExpandedSections] = useState(new Set())

  const toggleSection = (status) => {
    setExpandedSections(prev => {
      const next = new Set(prev)
      if (next.has(status)) next.delete(status)
      else next.add(status)
      return next
    })
  }

  // decode id_token to extract email
  function decodeIdTokenEmail() {
    try {
      const t = sessionStorage.getItem('id_token')
      if (!t) return null
      const payload = t.split('.')[1]
      const json = decodeURIComponent(atob(payload.replace(/-/g, '+').replace(/_/g, '/')).split('').map(function(c) {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)
      }).join(''))
      const p = JSON.parse(json)
      return (p.email || '').toString().toLowerCase()
    } catch (e) {
      console.warn('failed to decode id_token', e)
      return null
    }
  }

  async function fetchUserRecord(email) {
    try {
      const token = sessionStorage.getItem('id_token')
      const url = `${API_BASE}/reg-user?emailId=${encodeURIComponent(email)}`
      const resp = await fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      if (!resp.ok) return null
      const data = await resp.json()
      // Multi-community: pick the community the user selected (stored in sessionStorage)
      if (data.multiCommunity && Array.isArray(data.communities)) {
        const selectedCid = sessionStorage.getItem('community_id') || ''
        const selectedUid = sessionStorage.getItem('user_id') || ''
        return data.communities.find(c => {
          const cid = c.COMMUNITY_ID || c.communityId || ''
          const uid = c.USER_ID || c.userId || ''
          return selectedUid ? (cid === selectedCid && uid === selectedUid) : (cid === selectedCid)
        }) || data.communities[0] || null
      }
      return data
    } catch (e) {
      console.error('fetchUserRecord error', e)
      return null
    }
  }

  function mapApiDevice(item) {
    // API returns uppercase keys; normalize to UI model
    return {
      id: item.DEVICE_ID || item.deviceId || item.id,
      name: item.DEVICE_NAME || item.deviceName || item.name || '',
      type: item.DEVICE_TYPE || item.deviceType || item.type || 'device',
      status: (item.STATUS || item.status || 'offline').toString().toLowerCase(),
      lastUpdated: item.LAST_UPDATED || item.lastUpdated || new Date().toISOString(),
      details: item.details || '',
      updatedBy: item.UPDATED_BY || item.updatedBy || ''
    }
  }

  async function loadDevices() {
    setLoading(true)
    setError(null)
    
    try {
      const email = decodeIdTokenEmail()
      let userRecord = null
      if (email) {
        userRecord = await fetchUserRecord(email)
        setUser(userRecord)
      }

      if (!userRecord) {
        setError('Unable to retrieve user information')
        setDevices([])
        setLoading(false)
        return
      }

      const communityId = userRecord.COMMUNITY_ID || userRecord.communityId || userRecord.COMM_VISTRS_PRTN_KEY_ID
      if (!communityId) {
        setError('No community ID found for user')
        setDevices([])
        setLoading(false)
        return
      }

      const token = sessionStorage.getItem('id_token')
      const url = `${API_BASE}/devices?communityId=${encodeURIComponent(communityId)}`
      const resp = await fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      
      if (!resp.ok) {
        console.error('failed to fetch devices', resp.status)
        setDevices([])
        setLoading(false)
        return
      }

      const data = await resp.json()
      const mapped = (Array.isArray(data) ? data : []).map(mapApiDevice)
      setDevices(mapped)
    } catch (e) {
      console.error('loadDevices error', e)
      setError('Failed to load devices')
      setDevices([])
    }
    setLoading(false)
  }

  useEffect(() => {
    loadDevices()
  }, [])

  const getStatusColor = (status) => {
    switch (status) {
      case "operational":
        return "bg-green-100 border-green-300 text-green-800";
      case "maintenance":
        return "bg-yellow-100 border-yellow-300 text-yellow-800";
      case "offline":
        return "bg-red-100 border-red-300 text-red-800";
      default:
        return "";
    }
  };

  const getDeviceIcon = (type) => {
    switch (type) {
      case "lift":
        return <MdElevator size={24} />;
      case "generator":
        return <FaBolt size={24} />;
      default:
        return <FaCogs size={24} />;
    }
  };

  const handleAddDeviceSubmit = async (device) => {
    console.log("Device added/updated:", device);
    // Refresh the device list
    await loadDevices();
    setIsAddDeviceOpen(false);
  };

  const handleEditDevice = (device) => {
    setSelectedDeviceForUpdate(device);
    setIsUpdateDeviceOpen(true);
  };

  const handleUpdateDeviceSubmit = async (updatedDevice) => {
    console.log("Device status updated:", updatedDevice);
    // Refresh the device list
    await loadDevices();
    setIsUpdateDeviceOpen(false);
    setSelectedDeviceForUpdate(null);
  };

  return (
    <div className="p-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-bold">Smart Device Monitoring</h2>
        {isTrial !== false && trialMaxDevices != null && trialMaxDevices >= 0 && devices.length >= trialMaxDevices ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 12, color: '#92400e', background: '#fef3c7', border: '1px solid #fbbf24', borderRadius: 6, padding: '4px 10px' }}>
              🔒 Trial limit: {trialMaxDevices} devices
            </span>
          </div>
        ) : (
          <button
            onClick={() => setIsAddDeviceOpen(true)}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg flex items-center gap-2"
          >
            <FaPlus size={16} />
            <span>Add</span>
          </button>
        )}
      </div>
      {isTrial !== false && trialMaxDevices != null && trialMaxDevices >= 0 && devices.length >= trialMaxDevices && (
        <div style={{ background: '#fffbeb', border: '1px solid #fbbf24', borderRadius: 8, padding: '10px 14px', marginBottom: 12, fontSize: 13, color: '#92400e' }}>
          ⚠️ You've reached the <strong>{trialMaxDevices}-device limit</strong> on your free trial. Upgrade to add more devices.
        </div>
      )}

      {/* Add Device Modal */}
      <AddDeviceModal
        isOpen={isAddDeviceOpen}
        onClose={() => setIsAddDeviceOpen(false)}
        onSubmit={handleAddDeviceSubmit}
      />

      {/* Update Device Status Modal */}
      <UpdateDeviceStatusModal
        isOpen={isUpdateDeviceOpen}
        onClose={() => {
          setIsUpdateDeviceOpen(false);
          setSelectedDeviceForUpdate(null);
        }}
        onSubmit={handleUpdateDeviceSubmit}
        device={selectedDeviceForUpdate}
        userCommunity={user?.COMMUNITY_ID || user?.communityId || user?.COMM_VISTRS_PRTN_KEY_ID}
      />

      {/* Loading State */}
      {loading && (
        <div className="text-center py-8 text-gray-600">
          <p>Loading devices...</p>
        </div>
      )}

      {/* Error State */}
      {error && !loading && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      {/* Empty State */}
      {!loading && !error && devices.length === 0 && (
        <div className="text-center py-8 text-white">
          <p>No devices found for your community</p>
        </div>
      )}

      {/* Status Summary — tap to expand/collapse device list */}
      {!loading && devices.length > 0 && (
        <div className="space-y-3">
          {[
            { key: 'operational', label: 'Operational', color: 'green', borderColor: 'border-green-300', bgColor: 'bg-green-50' },
            { key: 'maintenance', label: 'Maintenance', color: 'yellow', borderColor: 'border-yellow-300', bgColor: 'bg-yellow-50' },
            { key: 'offline', label: 'Offline', color: 'red', borderColor: 'border-red-300', bgColor: 'bg-red-50' },
          ].map(({ key, label, color, borderColor, bgColor }) => {
            const sectionDevices = devices.filter(d => d.status === key)
            const isExpanded = expandedSections.has(key)
            return (
              <div key={key}>
                {/* Tappable summary card */}
                <button
                  onClick={() => toggleSection(key)}
                  className={`w-full ${bgColor} border-2 ${borderColor} rounded-lg p-4 flex items-center justify-between active:scale-[0.98] transition-transform`}
                >
                  <div className="flex items-center gap-3">
                    <span className={`text-2xl font-bold text-${color}-600`}>{sectionDevices.length}</span>
                    <span className={`text-sm font-medium text-${color}-800`}>{label}</span>
                  </div>
                  <span className={`text-${color}-600 text-lg`}>{isExpanded ? '▲' : '▼'}</span>
                </button>

                {/* Expanded device list */}
                {isExpanded && sectionDevices.length > 0 && (
                  <div className="mt-2 space-y-2 pl-2">
                    {sectionDevices.map(device => (
                      <div
                        key={device.id}
                        className={`border-2 rounded-lg p-4 ${getStatusColor(device.status)}`}
                      >
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex items-start gap-3">
                            <div className="mt-1">{getDeviceIcon(device.type)}</div>
                            <div>
                              <h3 className="mb-1">{device.name}</h3>
                              <p className="text-sm opacity-80">
                                {device.status.charAt(0).toUpperCase() + device.status.slice(1)}
                              </p>
                            </div>
                          </div>
                          <div className="flex gap-1">
                            <button
                              onClick={() => handleEditDevice(device)}
                              className="p-2 hover:bg-white/30 rounded-lg transition-colors"
                              title="Update Status"
                            >
                              <FaEdit size={16} />
                            </button>
                            <button
                              onClick={() => onDeleteDevice(device.id)}
                              className="p-2 hover:bg-white/30 rounded-lg transition-colors"
                              title="Delete Device"
                            >
                              <FaTrash size={16} />
                            </button>
                          </div>
                        </div>
                        {device.details && (
                          <p className="text-sm opacity-90 mb-2">{device.details}</p>
                        )}
                        <p className="text-xs opacity-70">
                          Last updated: {new Date(device.lastUpdated).toLocaleString()}
                        </p>
                      </div>
                    ))}
                  </div>
                )}

                {isExpanded && sectionDevices.length === 0 && (
                  <p className="mt-2 pl-2 text-sm text-gray-500">No {label.toLowerCase()} devices</p>
                )}
              </div>
            )
          })}
        </div>
      )}
    </div>
  );
}
export default SmartDevices;